/**
 * Just-in-time profile details for an action (registration, checkout, booking).
 *
 *   const gate = useProfileGate();
 *   if (!(await gate.ensure('shop_checkout'))) return;      // asks only if needed
 *   ...
 *   catch (e) { if (await gate.handleError(e)) retry(); }    // backend said profile_incomplete
 *   return <>{...}{gate.sheet}</>;
 *
 * Every function resolves true once the details are saved, false if the user
 * closed the sheet.
 */
import { useCallback, useState } from 'react';
import { axiosInstance } from '../config/axios';
import CompleteDetailsSheet from '../components/profile/CompleteDetailsSheet';

const REASONS = {
  tournament_registration: 'Organisers need these to contact you and confirm your registration.',
  shop_checkout: 'We need your mobile number for delivery updates.',
  service_booking: 'Our partner needs your mobile number to confirm the booking.',
};

export default function useProfileGate() {
  const [request, setRequest] = useState(null);   // { fields, required, reason, resolve }

  // Open the sheet for `missing` (required); `fields` may add optional extras (edit mode).
  const ask = useCallback((missing, { reason, fields } = {}) => new Promise((resolve) => {
    setRequest({ fields: fields || missing, required: missing, reason, resolve });
  }), []);

  const ensure = useCallback(async (action, params = {}) => {
    try {
      const res = await axiosInstance.get('/auth/requirements', { params: { action, ...params } });
      const missing = res.data?.missing || [];
      if (!missing.length) return true;
      return ask(missing, { reason: REASONS[action] });
    } catch {
      return true;   // can't check now — the backend still enforces it on submit
    }
  }, [ask]);

  const handleError = useCallback(async (err) => {
    const data = err?.response?.data;
    if (err?.response?.status !== 409 || data?.code !== 'profile_incomplete') return false;
    return ask(data.missing || [], { reason: data.detail });
  }, [ask]);

  const finish = (ok) => {
    request?.resolve(ok);
    setRequest(null);
  };

  const sheet = request ? (
    <CompleteDetailsSheet
      fields={request.fields}
      required={request.required}
      reason={request.reason}
      onDone={() => finish(true)}
      onCancel={() => finish(false)}
    />
  ) : null;

  return { ensure, ask, handleError, sheet };
}
